import type { ReactNode } from "react"

import { useColorModeValue } from "@/components/ui/color-mode"
import { Box, EmptyState, HStack, Stack, Text } from "@chakra-ui/react"

type EmptyStateVariant = "primary" | "compact" | "inline"
type EmptyStateTone = "neutral" | "info" | "warning" | "danger"

interface AppEmptyStateProps {
  title: string
  description?: string
  variant?: EmptyStateVariant
  tone?: EmptyStateTone
  icon?: ReactNode
  action?: ReactNode
  "data-testid"?: string
}

export function AppEmptyState({
  title,
  description,
  variant = "primary",
  tone = "neutral",
  icon,
  action,
  "data-testid": dataTestId,
}: AppEmptyStateProps) {
  const bg = useColorModeValue(
    {
      neutral: "gray.50",
      info: "blue.50",
      warning: "orange.50",
      danger: "red.50",
    }[tone],
    {
      neutral: "whiteAlpha.100",
      info: "blue.950",
      warning: "orange.950",
      danger: "red.950",
    }[tone],
  )

  const borderColor = useColorModeValue(
    {
      neutral: "gray.200",
      info: "blue.200",
      warning: "orange.200",
      danger: "red.200",
    }[tone],
    {
      neutral: "whiteAlpha.200",
      info: "blue.700",
      warning: "orange.700",
      danger: "red.700",
    }[tone],
  )

  const iconBg = useColorModeValue(
    {
      neutral: "gray.100",
      info: "blue.100",
      warning: "orange.100",
      danger: "red.100",
    }[tone],
    {
      neutral: "whiteAlpha.200",
      info: "blue.900",
      warning: "orange.900",
      danger: "red.900",
    }[tone],
  )

  if (variant === "inline") {
    return (
      <Box
        data-testid={dataTestId}
        bg={bg}
        borderRadius="md"
        color="fg.muted"
        fontSize="sm"
        px={3}
        py={3}
        textAlign="center"
      >
        <Text>{title}</Text>
        {description ? (
          <Text mt={1} fontSize="xs">
            {description}
          </Text>
        ) : null}
      </Box>
    )
  }

  if (variant === "compact") {
    return (
      <HStack
        data-testid={dataTestId}
        align="flex-start"
        bg={bg}
        borderColor={borderColor}
        borderWidth={1}
        borderRadius="md"
        gap={3}
        p={4}
      >
        {icon ? (
          <Box
            aria-hidden="true"
            bg={iconBg}
            borderRadius="full"
            boxSize="32px"
            color="fg.muted"
            display="grid"
            flexShrink={0}
            placeItems="center"
          >
            {icon}
          </Box>
        ) : null}
        <Stack gap={1}>
          <Text fontWeight="semibold">{title}</Text>
          {description ? (
            <Text color="fg.muted" fontSize="sm">
              {description}
            </Text>
          ) : null}
          {action ? <Box pt={2}>{action}</Box> : null}
        </Stack>
      </HStack>
    )
  }

  return (
    <Box
      data-testid={dataTestId}
      bg={bg}
      borderColor={borderColor}
      borderRadius="lg"
      borderWidth={1}
      p={{ base: 6, md: 10 }}
    >
      <EmptyState.Root>
        <EmptyState.Content>
          {icon ? (
            <EmptyState.Indicator>
              <Box
                aria-hidden="true"
                bg={iconBg}
                borderRadius="full"
                boxSize="44px"
                display="grid"
                placeItems="center"
              >
                {icon}
              </Box>
            </EmptyState.Indicator>
          ) : null}
          <EmptyState.Title>{title}</EmptyState.Title>
          {description ? (
            <EmptyState.Description>{description}</EmptyState.Description>
          ) : null}
          {action ? <Box pt={2}>{action}</Box> : null}
        </EmptyState.Content>
      </EmptyState.Root>
    </Box>
  )
}
